import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { aclAPI, ACLPermission, ACLAction, getPermissionActions, FORM_CONFIG_ACTIONS } from '../../api/aclApi';
import { clearPermissionCache } from '../../acl/usePermission';
import { ActionTypes } from '@/actions';
import { dataAPI } from '../../api/dataApi';
import { fetchFormConfigs } from '../../api/formConfigApi';
import { connectorsAPI } from '../../api/connectorsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectLabel,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { X, Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import {
    ItemSelectorField,
    type ItemSelectorItem,
    type ItemSelectorTableColumn,
} from '@/components/ItemSelectorDialog';

type SearchOption = {
    value: string;
    label: string;
    subtitle?: string;
    selectedLabel?: string;
    searchText?: string;
};

import { resolveApiBaseUrl } from '../../api/apiBase';

const API_BASE_URL = resolveApiBaseUrl();

interface PermissionFormProps {
    permission?: ACLPermission | null;
    onSave: () => void;
    onCancel: () => void;
}

const WHO_TYPES = ['UID', 'GROUPID', 'ROLE'] as const;
const ROLE_OPTIONS = ['user', 'public'] as const;
const WHAT_TYPES = ['ItemId', 'CollectionId', 'NewCollection', 'ItemIdSet', 'CollectionIdSet', 'AllItemsInCollectionId', 'FormId', 'FormIdSet', 'AllFormsInCollectionId', 'ConnectorKey'] as const;
const WHAT_TYPE_LABELS: Record<typeof WHAT_TYPES[number], string> = {
    ItemId: 'Item (ID)',
    CollectionId: 'Collection (ID)',
    NewCollection: 'New collection (any)',
    ItemIdSet: 'Item set (IDs)',
    CollectionIdSet: 'Collection set (IDs)',
    AllItemsInCollectionId: 'All items in collection',
    FormId: 'Form configuration (ID)',
    FormIdSet: 'Form configuration set (IDs)',
    AllFormsInCollectionId: 'All form configs for collection',
    ConnectorKey: 'Connector (key)',
};
const ACTIONS: ACLAction[] = [
    'view', 'list', 'listItems', 'create', 'create_collection', 'read', 'update', 'delete', 'delegate',
    'field_read', 'field_update',
    'edit_collection_name', 'edit_collection_name_suggestion', 'view_collection_advanced',
    'public_publish_collection', 'public_expose_fields', 'public_form', 'all_users_access_collection', 'all_users_expose_fields',
    ...FORM_CONFIG_ACTIONS
];
const LEGACY_ACTIONS: ACLAction[] = ['relate', 'publish', 'archive', 'share', 'public_export'];
const ACTION_LABELS: Partial<Record<ACLAction, string>> = {
    view: 'View items',
    list: 'List collections (name/count row)',
    listItems: 'List items',
    create: 'Create items',
    create_collection: 'Create collections',
    read: 'Read items',
    update: 'Update items',
    delete: 'Delete items',
    delegate: 'Delegate ownership/rights',
    field_read: 'Read fields',
    field_update: 'Update fields',
    edit_collection_name: 'Edit collection name',
    edit_collection_name_suggestion: 'Suggest collection name',
    view_collection_advanced: 'View advanced collection settings',
    public_publish_collection: 'Public: publish full collection',
    public_expose_fields: 'Public: expose specific fields',
    public_form: 'Public: form access (published forms)',
    all_users_access_collection: 'All users: access full collection',
    all_users_expose_fields: 'All users: expose specific fields',
    create_form: 'Create form configurations',
    read_form: 'Read form configurations',
    update_form: 'Update form configurations',
    delete_form: 'Delete form configurations',
};
const ACTION_HELP_TEXT: Partial<Record<ACLAction, string>> = {
    public_publish_collection: 'Unauthenticated public audience. Publishes the full collection payload.',
    public_expose_fields: 'Unauthenticated public audience. Grants field-level exposure without full collection publish.',
    public_form: 'Unauthenticated public audience. Allows published /forms/{token} flows for this collection (fields from form config). Does not unlock generic public read feeds.',
    all_users_access_collection: 'Authenticated global audience. Grants full collection access for logged-in users.',
    all_users_expose_fields: 'Authenticated global audience. Grants field-level access for logged-in users.',
    create_form: 'Create public form configs (public_forms). Does not grant collection item CRUD.',
    read_form: 'View public form configs. Does not grant collection item read access.',
    update_form: 'Edit public form configs. Does not grant collection item update access.',
    delete_form: 'Delete public form configs. Does not grant collection item delete access.',
};
const ACTION_MENU_SUBTITLES: Partial<Record<ACLAction, string>> = {
    view: 'Open item detail screens.',
    list: 'See collection shell rows (name and item count only).',
    listItems: 'Query and enumerate collection rows.',
    create: 'Create new items in the target.',
    create_collection: 'Create new collections.',
    read: 'Read item payload data.',
    update: 'Edit existing item data.',
    delete: 'Delete existing items.',
    delegate: 'Assign owner/delegate rights.',
    field_read: 'Allow reading selected fields.',
    field_update: 'Allow updating selected fields.',
    edit_collection_name: 'Rename an existing collection.',
    edit_collection_name_suggestion: 'Use name suggestion endpoint.',
    view_collection_advanced: 'Open advanced collection settings.',
    public_publish_collection: 'Publish full payload to unauthenticated endpoints.',
    public_expose_fields: 'Publish only selected fields to unauthenticated endpoints.',
    public_form: 'Enable public form UX for this collection per form configuration.',
    all_users_access_collection: 'Grant full collection access to logged-in users.',
    all_users_expose_fields: 'Grant selected-field access to logged-in users.',
    create_form: 'Create new public form configs for a target collection.',
    read_form: 'List and open public form configs.',
    update_form: 'Edit existing public form configs.',
    delete_form: 'Remove public form configs.',
    relate: 'Use relation lookup/options (legacy).',
    public_export: 'Allow old public export path (legacy).',
    publish: 'Legacy publish behavior.',
    archive: 'Legacy archive behavior.',
    share: 'Legacy share behavior.',
};
const ACTION_GROUPS = {
    publicAudience: ['public_publish_collection', 'public_expose_fields', 'public_form'] as ACLAction[],
    allUsersAudience: ['all_users_access_collection', 'all_users_expose_fields'] as ACLAction[],
    formsConfig: [...FORM_CONFIG_ACTIONS] as ACLAction[],
};

const ACTIONS_BY_CATEGORY = {
    /** Navigation / component metadata; runtime matcher treats CollectionId like AllItemsInCollectionId for items, so we only list collection refs here to avoid duplicate-looking options. */
    collectionShell: ['view', 'list', 'listItems', 'edit_collection_name', 'edit_collection_name_suggestion', 'view_collection_advanced'] as const,
};

const COLLECTION_REF_WHAT = ['CollectionId', 'CollectionIdSet'] as const;
const ALL_ITEMS_IN_COLLECTION_WHAT = ['AllItemsInCollectionId'] as const;
const ITEM_SCOPED_WHAT = ['ItemId', 'ItemIdSet'] as const;

const FORM_SCOPED_WHAT = ['FormId', 'FormIdSet', 'AllFormsInCollectionId'] as const;
const FORM_COLLECTION_WHAT = ['AllFormsInCollectionId'] as const;

const COLLECTION_SELECTOR_TABLE_COLUMNS: ItemSelectorTableColumn[] = [
    { key: 'label', header: 'Name' },
    { key: 'slug', header: 'Slug' },
    { key: 'uid', header: 'UID' },
];

const FORM_SELECTOR_TABLE_COLUMNS: ItemSelectorTableColumn[] = [
    { key: 'label', header: 'Title' },
    { key: 'collection', header: 'Collection' },
    { key: 'uid', header: 'UID' },
];

const WILDCARD_SELECTOR_ITEM: ItemSelectorItem = {
    value: '*',
    label: 'All resources',
    subtitle: 'Wildcard',
    cells: { label: 'All resources', slug: '*', uid: '*' },
};

function parseWhatSetValue(raw: string): string[] {
    return raw.split(',').map((part) => part.trim()).filter(Boolean);
}

function formatWhatSetValue(values: string[]): string {
    return values.map((part) => part.trim()).filter(Boolean).join(', ');
}

function searchOptionToSelectorItem(option: SearchOption): ItemSelectorItem {
    const slugMatch = option.subtitle?.match(/Slug:\s*([^•]+)/);
    const uidMatch = option.subtitle?.match(/UID:\s*(\S+)/);
    const collectionMatch = option.subtitle?.match(/Collection:\s*([^•]+)/);
    return {
        value: option.value,
        label: option.label,
        subtitle: option.subtitle,
        cells: {
            label: option.label,
            slug: slugMatch?.[1]?.trim() ?? '',
            uid: uidMatch?.[1]?.trim() ?? option.value,
            collection: collectionMatch?.[1]?.trim() ?? '',
        },
    };
}

function isFormConfigAction(action: ACLAction): boolean {
    return (FORM_CONFIG_ACTIONS as readonly string[]).includes(action);
}

/** Actions offered for guest-facing roles (narrower, safer defaults). */
function actionsForPrincipal(whoType: typeof WHO_TYPES[number], whoTrimmed: string): ACLAction[] {
    const who = whoTrimmed.toLowerCase();
    if (whoType === 'ROLE' && (who === 'public' || who === 'visitor')) {
        const allow = new Set<ACLAction>([
            'view', 'list', 'listItems', 'read', 'relate', 'field_read', 'field_update', 'public_export',
            'public_publish_collection', 'public_expose_fields', 'public_form',
        ]);
        return ACTIONS.filter(a => allow.has(a));
    }
    return ACTIONS;
}

const CONNECTOR_ACTIONS: ReadonlySet<string> = new Set(['view', 'listItems', 'list', 'create', 'read', 'update', 'delete']);

function whatTypesForAction(action: ACLAction): Array<typeof WHAT_TYPES[number]> {
    if (action === 'create_collection') {
        return ['NewCollection'];
    }
    if (action === 'create_form') {
        return [...FORM_COLLECTION_WHAT];
    }
    if (isFormConfigAction(action)) {
        return [...FORM_SCOPED_WHAT];
    }
    const collectionOnly = ACTIONS_BY_CATEGORY.collectionShell as readonly string[];
    if (collectionOnly.includes(action)) {
        return [...COLLECTION_REF_WHAT, 'ConnectorKey'];
    }
    if (CONNECTOR_ACTIONS.has(action)) {
        return [...COLLECTION_REF_WHAT, ...ALL_ITEMS_IN_COLLECTION_WHAT, ...ITEM_SCOPED_WHAT, 'ConnectorKey'];
    }
    return [...COLLECTION_REF_WHAT, ...ALL_ITEMS_IN_COLLECTION_WHAT, ...ITEM_SCOPED_WHAT];
}

const SearchableSelect = ({
    value,
    onSelect,
    options,
    placeholder,
    emptyText,
    disabled,
    onCreateOption,
    createOptionLabel,
    selectedValueFormatter,
}: {
    value: string;
    onSelect: (value: string) => void;
    options: SearchOption[];
    placeholder: string;
    emptyText?: string;
    disabled?: boolean;
    onCreateOption?: (value: string) => Promise<string | void> | string | void;
    createOptionLabel?: string;
    selectedValueFormatter?: (value: string, selectedOption?: SearchOption) => string;
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const selectedLabel = useMemo(() => {
        const selected = options.find(option => option.value === value);
        if (selectedValueFormatter) {
            return selectedValueFormatter(value, selected);
        }
        return selected?.selectedLabel || selected?.label || value;
    }, [options, value, selectedValueFormatter]);

    const filteredOptions = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return options;
        return options.filter(option => {
            const haystack = `${option.label} ${option.value} ${option.searchText || ''}`.toLowerCase();
            return haystack.includes(term);
        });
    }, [options, search]);

    const normalizedSearch = search.trim();
    const canCreateOption = useMemo(() => {
        if (!onCreateOption || !normalizedSearch) return false;
        const searchLower = normalizedSearch.toLowerCase();
        return !options.some(option => {
            return option.value.toLowerCase() === searchLower || option.label.toLowerCase() === searchLower;
        });
    }, [onCreateOption, normalizedSearch, options]);

    return (
        <Popover open={open} onOpenChange={setOpen} modal={false}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    disabled={disabled}
                    className="w-full justify-between"
                >
                    <span className={`flex-1 text-left truncate pr-2 ${value ? '' : 'text-muted-foreground'}`}>
                        {value ? selectedLabel : placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="z-[100002] flex max-h-[min(340px,70vh)] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden p-0"
                onWheel={(e) => e.stopPropagation()}
            >
                <Command className="flex max-h-[min(340px,70vh)] min-h-0 flex-col overflow-hidden rounded-md bg-popover text-popover-foreground">
                    <CommandInput
                        placeholder="Search..."
                        value={search}
                        onValueChange={setSearch}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && filteredOptions.length === 1) {
                                event.preventDefault();
                                const only = filteredOptions[0];
                                onSelect(only.value);
                                setOpen(false);
                                return;
                            }
                            if (event.key === 'Enter' && canCreateOption && onCreateOption) {
                                event.preventDefault();
                                Promise.resolve(onCreateOption(normalizedSearch))
                                    .then((createdValue) => {
                                        onSelect(createdValue || normalizedSearch);
                                    })
                                    .finally(() => {
                                        setSearch('');
                                        setOpen(false);
                                    });
                            }
                        }}
                    />
                    <CommandList className="min-h-0 flex-1 overflow-y-auto overscroll-contain scroll-smooth">
                        <CommandEmpty>{emptyText || 'No results found.'}</CommandEmpty>
                        {filteredOptions.map(option => (
                            <CommandItem
                                key={option.value}
                                value={`${option.label} ${option.value} ${option.searchText || ''}`}
                                onSelect={() => {
                                    onSelect(option.value);
                                    setOpen(false);
                                }}
                            >
                                <Check
                                    className={`mr-2 h-4 w-4 ${option.value === value ? 'opacity-100' : 'opacity-0'}`}
                                />
                                <div className="flex flex-col items-start">
                                    <span>{option.label}</span>
                                    {option.subtitle && (
                                        <span className="text-xs text-muted-foreground">{option.subtitle}</span>
                                    )}
                                </div>
                            </CommandItem>
                        ))}
                        {canCreateOption && onCreateOption && (
                            <>
                                <CommandSeparator />
                                <CommandItem
                                    value={`create ${normalizedSearch}`}
                                    onSelect={() => {
                                        Promise.resolve(onCreateOption(normalizedSearch))
                                            .then((createdValue) => {
                                                onSelect(createdValue || normalizedSearch);
                                            })
                                            .finally(() => {
                                                setSearch('');
                                                setOpen(false);
                                            });
                                    }}
                                >
                                    <span>{createOptionLabel || 'Add new'}: {normalizedSearch}</span>
                                </CommandItem>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export const PermissionForm: React.FC<PermissionFormProps> = ({ permission, onSave, onCancel }) => {
    const dispatch = useDispatch();
    const [whoType, setWhoType] = useState<typeof WHO_TYPES[number]>('ROLE');
    const [who, setWho] = useState('');
    const [action, setAction] = useState<ACLAction>('read');
    const [whatType, setWhatType] = useState<typeof WHAT_TYPES[number]>('CollectionId');
    const [what, setWhat] = useState('');
    const [includeFields, setIncludeFields] = useState<string[]>([]);
    const [componentsByUid, setComponentsByUid] = useState<
        Record<string, { slug?: string; fields: Array<{ name?: string; key?: string; label?: string }> }>
    >({});
    const [fieldPickerOpen, setFieldPickerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [userOptions, setUserOptions] = useState<SearchOption[]>([]);
    const [groupOptions, setGroupOptions] = useState<SearchOption[]>([]);
    const [componentOptions, setComponentOptions] = useState<SearchOption[]>([]);
    const [formOptions, setFormOptions] = useState<SearchOption[]>([]);
    const [connectorOptions, setConnectorOptions] = useState<SearchOption[]>([]);
    const isCollectionSelectorType = useMemo(() => {
        return whatType === 'CollectionId' || whatType === 'CollectionIdSet' || whatType === 'AllItemsInCollectionId' || whatType === 'AllFormsInCollectionId';
    }, [whatType]);
    const isFormSelectorType = useMemo(() => {
        return whatType === 'FormId' || whatType === 'FormIdSet';
    }, [whatType]);
    const isConnectorSelectorType = whatType === 'ConnectorKey';
    const isWhatSetType = whatType.includes('Set');
    const isCreateCollectionAction = action === 'create_collection';

    const componentSelectorItems = useMemo((): ItemSelectorItem[] => {
        const items = componentOptions.map(searchOptionToSelectorItem);
        if (!isWhatSetType) {
            return [WILDCARD_SELECTOR_ITEM, ...items];
        }
        return items;
    }, [componentOptions, isWhatSetType]);

    const formSelectorItems = useMemo(
        (): ItemSelectorItem[] => formOptions.map(searchOptionToSelectorItem),
        [formOptions]
    );

    const collectionSelectorValue = useMemo(
        () => (isWhatSetType ? parseWhatSetValue(what) : what),
        [what, isWhatSetType]
    );

    const formSelectorValue = useMemo(
        () => (isWhatSetType ? parseWhatSetValue(what) : what),
        [what, isWhatSetType]
    );

    const handleWhoTypeChange = (nextType: typeof WHO_TYPES[number]) => {
        if (nextType !== whoType) {
            // Reset identifier to avoid showing stale values from a different principal type.
            setWho('');
        }
        setWhoType(nextType);
    };

    const principalReady = who.trim().length > 0;
    const actionHelp = ACTION_HELP_TEXT[action];
    const renderActionOption = (act: ACLAction) => (
        <div className="flex flex-col items-start py-0.5 text-left">
            <span>{ACTION_LABELS[act] || act}</span>
            {ACTION_MENU_SUBTITLES[act] && (
                <span className="text-xs text-muted-foreground">{ACTION_MENU_SUBTITLES[act]}</span>
            )}
        </div>
    );

    const allowedActions = useMemo(() => {
        const fromPrincipal = actionsForPrincipal(whoType, who.trim());
        if (action && !fromPrincipal.includes(action)) {
            const merged = new Set([...fromPrincipal, action]);
            return ACTIONS.filter(a => merged.has(a));
        }
        return fromPrincipal;
    }, [whoType, who, action]);
    const groupedAllowedActions = useMemo(() => {
        const isInAllowed = (act: ACLAction) => allowedActions.includes(act);
        const publicAudience = ACTION_GROUPS.publicAudience.filter(isInAllowed);
        const allUsersAudience = ACTION_GROUPS.allUsersAudience.filter(isInAllowed);
        const formsConfig = ACTION_GROUPS.formsConfig.filter(isInAllowed);
        const grouped = new Set<ACLAction>([...publicAudience, ...allUsersAudience, ...formsConfig]);
        const general = allowedActions.filter((act) => !grouped.has(act));
        const legacy = LEGACY_ACTIONS.filter((act) => allowedActions.includes(act));
        const generalNoLegacy = general.filter((act) => !legacy.includes(act));
        return { publicAudience, allUsersAudience, formsConfig, general: generalNoLegacy, legacy };
    }, [allowedActions]);

    const allowedWhatTypes = useMemo(() => {
        if (action === 'create_collection') {
            return WHAT_TYPES.filter(t => t === 'NewCollection');
        }
        const base = whatTypesForAction(action);
        const merged = new Set<typeof WHAT_TYPES[number]>(base);
        if (whatType !== 'NewCollection' && !merged.has(whatType)) {
            merged.add(whatType);
        }
        return WHAT_TYPES.filter(t => t !== 'NewCollection' && merged.has(t));
    }, [action, whatType]);

    useEffect(() => {
        const allowed = actionsForPrincipal(whoType, who.trim());
        if (allowed.length === 0) return;
        if (!allowed.includes(action)) {
            setAction(allowed[0]);
        }
    }, [whoType, who]);

    useEffect(() => {
        if (permission) return;
        if (action === 'create_collection') {
            setWhatType('NewCollection');
            setWhat('*');
            return;
        }
        const allowed = whatTypesForAction(action);
        if (!allowed.includes(whatType)) {
            setWhatType(allowed[0] as typeof WHAT_TYPES[number]);
            setWhat('');
        }
    }, [action, whatType, permission]);

    useEffect(() => {
        let isMounted = true;

        const loadOptions = async () => {
            try {
                const [usersResponse, componentsResponse] = await Promise.all([
                    dataAPI.getUsersList(),
                    dataAPI.getComponents()
                ]);

                if (!isMounted) return;

                const users = Array.isArray(usersResponse?.peers) ? usersResponse.peers : [];
                const userOptionsData = users
                    .map((user: any) => {
                        const uid = user?.uid || user?.id || user?._id;
                        if (!uid) return null;
                        const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
                        const email = user?.email || '';
                        const primary = name || 'Unknown User';
                        const selectedLabel = email ? `${primary} (${email})` : primary;
                        return {
                            value: uid,
                            label: selectedLabel,
                            subtitle: `UID: ${uid}`,
                            selectedLabel,
                            searchText: [name, email, uid].filter(Boolean).join(' ')
                        };
                    })
                    .filter(Boolean) as SearchOption[];

                const rawComponents = componentsResponse?.data || componentsResponse?.components || [];
                const list = Array.isArray(rawComponents) ? rawComponents : [];
                const byUid: Record<
                    string,
                    { slug?: string; fields: Array<{ name?: string; key?: string; label?: string }> }
                > = {};
                for (const component of list) {
                    const uid = String(component?.uid || component?.id || component?._id || '').trim();
                    if (!uid) continue;
                    byUid[uid] = {
                        slug: typeof component?.slug === 'string' ? component.slug : undefined,
                        fields: Array.isArray(component?.fields) ? component.fields : [],
                    };
                }
                setComponentsByUid(byUid);

                const componentOptionsData = list
                    .map((component: any) => {
                        const uid = component?.uid || component?.id || component?._id;
                        const slug = component?.slug;
                        if (!uid && !slug) return null;
                        const name = component?.name || slug;
                        const subtitle = [slug ? `Slug: ${slug}` : '', uid ? `UID: ${uid}` : '']
                            .filter(Boolean)
                            .join(' • ');
                        return {
                            value: uid || slug,
                            label: name,
                            subtitle,
                            selectedLabel: name,
                            searchText: [name, slug, uid].filter(Boolean).join(' ')
                        };
                    })
                    .filter(Boolean) as SearchOption[];

                setUserOptions(userOptionsData);
                setComponentOptions(componentOptionsData);

                try {
                    const forms = await fetchFormConfigs();
                    if (!isMounted) return;
                    const formOptionsData = (forms || [])
                        .map((form) => {
                            const uid = form?.uid;
                            if (!uid) return null;
                            const title = form.title || uid;
                            const subtitle = [form.componentSlug ? `Collection: ${form.componentSlug}` : '', `UID: ${uid}`]
                                .filter(Boolean)
                                .join(' • ');
                            return {
                                value: uid,
                                label: title,
                                subtitle,
                                selectedLabel: title,
                                searchText: [title, uid, form.componentSlug, form.componentUid].filter(Boolean).join(' ')
                            };
                        })
                        .filter(Boolean) as SearchOption[];
                    setFormOptions(formOptionsData);
                } catch (formLoadError) {
                    console.error('Failed to load form options for ACL editor:', formLoadError);
                    if (isMounted) {
                        setFormOptions([]);
                    }
                }

                try {
                    const connectorsRes = await connectorsAPI.list();
                    if (!isMounted) return;
                    const connectorOptionsData = (connectorsRes.connectors || [])
                        .map((connector) => ({
                            value: connector.key,
                            label: connector.label || connector.key,
                            subtitle: `Key: ${connector.key}`,
                            selectedLabel: connector.label || connector.key,
                            searchText: [connector.label, connector.key].filter(Boolean).join(' ')
                        }));
                    setConnectorOptions([
                        { value: '*', label: 'All connectors', subtitle: 'Wildcard', selectedLabel: 'All connectors', searchText: '* all connectors' },
                        ...connectorOptionsData
                    ]);
                } catch {
                    if (isMounted) setConnectorOptions([]);
                }
            } catch (loadError) {
                console.error('Failed to load ACL dropdown options:', loadError);
                if (isMounted) {
                    setUserOptions([]);
                    setGroupOptions([]);
                    setComponentOptions([]);
                }
            }
        };

        loadOptions();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (permission) {
            setWhoType(permission.whoType);
            setWho(permission.who);
            const acts = getPermissionActions(permission);
            setAction((acts[0] as ACLAction) ?? 'read');
            const storedWhatType = permission.whatType;
            const storedWhat = Array.isArray(permission.what) ? permission.what.join(', ') : permission.what;
            const actList = getPermissionActions(permission);
            if (actList.includes('create_collection') && storedWhatType === 'CollectionId' && storedWhat === '*') {
                setWhatType('NewCollection');
                setWhat('*');
            } else {
                setWhatType(storedWhatType);
                setWhat(storedWhat);
            }
            setIncludeFields(permission.includeFields || []);
        }
    }, [permission]);

    const resolvedCollectionUids = useMemo(() => {
        const resolveTokenToId = (token: string): string => {
            const trimmed = token.trim();
            if (!trimmed || trimmed === '*') return '';
            const lower = trimmed.toLowerCase();
            const exact = componentOptions.find((o) => o.value === trimmed);
            if (exact) return String(exact.value).trim();
            const match = componentOptions.find((option) => {
                const haystack = `${option.label} ${option.value} ${option.searchText || ''}`.toLowerCase();
                return haystack.includes(lower);
            });
            return String(match?.value || trimmed).trim();
        };

        if (whatType === 'CollectionId' || whatType === 'AllItemsInCollectionId') {
            const id = resolveTokenToId(what);
            return id ? [id] : [];
        }
        if (whatType === 'CollectionIdSet') {
            return what
                .split(',')
                .map((s) => resolveTokenToId(s))
                .filter(Boolean);
        }
        return [];
    }, [what, whatType, componentOptions]);

    const collectionFieldPickOptions = useMemo(() => {
        const names = new Map<string, string>();
        const lookupMeta = (id: string) => {
            const fromUid = componentsByUid[id];
            if (fromUid) return fromUid;
            const idLower = id.toLowerCase();
            return Object.values(componentsByUid).find((c) => c.slug?.toLowerCase() === idLower);
        };
        let hadCollectionMeta = false;
        for (const collUid of resolvedCollectionUids) {
            const meta = lookupMeta(collUid);
            if (!meta) continue;
            hadCollectionMeta = true;
            if (!meta.fields?.length) continue;
            for (const f of meta.fields) {
                const name = String(f.name || f.key || '').trim();
                if (!name || names.has(name)) continue;
                const lab = typeof f.label === 'string' ? f.label.trim() : '';
                names.set(name, lab && lab !== name ? `${name} — ${lab}` : name);
            }
        }
        // Item `uid` exists on every document but is often omitted from component.fields; still allow ACL allowlists to target it.
        if (hadCollectionMeta && !names.has('uid')) {
            names.set('uid', 'uid — Item id');
        }
        return [...names.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([value, label]) => ({ value, label }));
    }, [resolvedCollectionUids, componentsByUid]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!who.trim()) {
            setError('Choose a principal type and identifier first.');
            return;
        }
        setSaving(true);

        try {
            // Parse 'what' field - split by comma if it's a set type
            let whatValue: string | string[] = what.trim();
            if (whatType === 'ItemIdSet' || whatType === 'CollectionIdSet') {
                whatValue = what.split(',').map(s => s.trim()).filter(s => s);
            }

            // For collection-based WHAT types, resolve any slugs or labels to canonical component IDs
            if (whatType === 'CollectionId' || whatType === 'CollectionIdSet') {
                const resolveTokenToId = (token: string): string => {
                    const trimmed = token.trim();
                    if (!trimmed || trimmed === '*') return trimmed;

                    const lower = trimmed.toLowerCase();
                    const match = componentOptions.find(option => {
                        const haystack = `${option.label} ${option.value} ${option.searchText || ''}`.toLowerCase();
                        return haystack.includes(lower);
                    });

                    return match?.value || trimmed;
                };

                if (Array.isArray(whatValue)) {
                    whatValue = whatValue.map(resolveTokenToId);
                } else {
                    whatValue = resolveTokenToId(whatValue);
                }
            }

            // create_collection: collection ID not relevant, always use CollectionId + *
            const effectiveWhatType = whatType === 'NewCollection' ? 'CollectionId' : whatType;
            const effectiveWhat = whatType === 'NewCollection' ? '*' : whatValue;

            const permissionData = {
                whoType,
                who: who.trim(),
                action,
                whatType: effectiveWhatType,
                what: effectiveWhat,
                includeFields,
                excludeFields: [] as string[],
            };

            if (permission) {
                await aclAPI.updatePermission(permission.uid, permissionData);
            } else {
                try {
                    const analysis = await aclAPI.analyzeAcl({ drafts: [permissionData] });
                    const hint = analysis.drafts?.[0];
                    if (hint?.redundant && hint.message) {
                        const proceed = window.confirm(
                            `This grant may add no effective access (${hint.reasonCode ?? 'redundant'}):\n${hint.message}\n\nSave anyway?`
                        );
                        if (!proceed) {
                            return;
                        }
                    }
                } catch (analyzeErr) {
                    console.warn('ACL analyze before create failed:', analyzeErr);
                }
                await aclAPI.createPermission(permissionData);
            }

            clearPermissionCache();
            dispatch({ type: ActionTypes.UI_RELOAD_CONFIG });

            onSave();
        } catch (err: any) {
            console.error('Error saving permission:', err);
            setError(err.message || 'Failed to save permission');
        } finally {
            setSaving(false);
        }
    };

    const toggleIncludeField = (fieldName: string) => {
        setIncludeFields((prev) => {
            if (prev.includes(fieldName)) {
                return prev.filter((f) => f !== fieldName);
            }
            return [...prev, fieldName].sort((a, b) => a.localeCompare(b));
        });
    };

    const removeIncludeField = (field: string) => {
        setIncludeFields((prev) => prev.filter((f) => f !== field));
    };

    const loadGroupOptions = async () => {
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
            if (!token) {
                setGroupOptions([]);
                return;
            }

            const response = await fetch(`${API_BASE_URL}/auth/admin/groups`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to load groups' }));
                throw new Error(errorData.message || 'Failed to load groups');
            }
            const payload = await response.json();
            const groups = Array.isArray(payload?.groups) ? payload.groups : [];
            const groupOptionsData = groups
                .map((group: any) => {
                    const rawUid = group?.uid || group?.id || group?._id;
                    const uid = typeof rawUid === 'string' ? rawUid.trim() : rawUid;
                    if (!uid) return null;
                    const name = group?.title || group?.name || group?.slug || 'Group';
                    return {
                        value: uid,
                        label: name,
                        subtitle: `UID: ${uid}`,
                        selectedLabel: name,
                        searchText: [name, uid, group?.slug, group?.description].filter(Boolean).join(' ')
                    };
                })
                .filter(Boolean) as SearchOption[];
            setGroupOptions(groupOptionsData);
        } catch (loadError) {
            console.error('Failed to load group options:', loadError);
            setGroupOptions([]);
        }
    };

    const handleCreateGroupOption = async (groupValue: string): Promise<string> => {
        try {
            const trimmed = groupValue.trim();
            if (!trimmed) return '';

            const existingMatch = groupOptions.find(option =>
                option.value.toLowerCase() === trimmed.toLowerCase() ||
                option.label.toLowerCase() === trimmed.toLowerCase()
            );
            if (existingMatch) {
                return existingMatch.value;
            }

            const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(`${API_BASE_URL}/auth/admin/groups`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ title: trimmed, description: '' })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to create group' }));
                throw new Error(errorData.message || 'Failed to create group');
            }

            const payload = await response.json();
            const created = payload?.group;
            const createdUid = typeof created?.uid === 'string' ? created.uid.trim() : '';
            const createdTitle = typeof created?.title === 'string' && created.title.trim()
                ? created.title.trim()
                : trimmed;
            const resolvedUid = createdUid || trimmed;

            setGroupOptions(prev => {
                const exists = prev.some(option => option.value.toLowerCase() === resolvedUid.toLowerCase());
                if (exists) return prev;
                return [
                    {
                        value: resolvedUid,
                        label: createdTitle,
                        subtitle: `UID: ${resolvedUid}`,
                        selectedLabel: createdTitle,
                        searchText: [createdTitle, resolvedUid].join(' ')
                    },
                    ...prev
                ];
            });

            return resolvedUid;
        } catch (createError: any) {
            console.error('Failed to create group option:', createError);
            setError(createError?.message || 'Failed to create group');
            return '';
        }
    };

    useEffect(() => {
        if (whoType === 'GROUPID' && groupOptions.length === 0) {
            loadGroupOptions();
        }
    }, [whoType, groupOptions.length]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>{permission ? 'Edit Permission' : 'New Permission'}</CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                            {error}
                        </div>
                    )}

                    {/* WHO Section */}
                    <div className="space-y-2">
                        <Label className="text-base font-semibold">WHO can perform this action?</Label>
                        <p className="text-xs text-muted-foreground">
                            Choose principal type and identity first; action and resource types are limited to combinations that match the policy model.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="whoType">Type</Label>
                                <Select value={whoType} onValueChange={(value: any) => handleWhoTypeChange(value)}>
                                    <SelectTrigger id="whoType">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {WHO_TYPES.map(type => (
                                            <SelectItem key={type} value={type}>
                                                {type === 'UID' ? 'User' : type === 'GROUPID' ? 'Group' : type}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="who">Identifier</Label>
                                {whoType === 'UID' ? (
                                    <SearchableSelect
                                        value={who}
                                        onSelect={setWho}
                                        options={userOptions}
                                        placeholder="Select a user"
                                        emptyText="No users found."
                                    />
                                ) : whoType === 'GROUPID' ? (
                                    <SearchableSelect
                                        value={who}
                                        onSelect={setWho}
                                        options={groupOptions}
                                        placeholder="Select a group"
                                        emptyText="No groups found."
                                        onCreateOption={handleCreateGroupOption}
                                        createOptionLabel="Add new group"
                                        selectedValueFormatter={(rawValue, selectedOption) => {
                                            if (selectedOption?.selectedLabel) {
                                                return selectedOption.selectedLabel;
                                            }
                                            const trimmed = rawValue.trim();
                                            const sep = ' • ';
                                            if (trimmed.includes(sep)) {
                                                return trimmed.split(sep)[0].trim();
                                            }
                                            return trimmed;
                                        }}
                                    />
                                ) : (
                                    <Select value={who} onValueChange={(value: any) => setWho(value)}>
                                        <SelectTrigger id="who">
                                            <SelectValue placeholder="Select a role" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {who === 'admin' && (
                                                <SelectItem value="admin">admin (legacy)</SelectItem>
                                            )}
                                            {(who === 'member' || who === 'visitor') && (
                                                <SelectItem value={who}>{who} (legacy)</SelectItem>
                                            )}
                                            {ROLE_OPTIONS.map(role => (
                                                <SelectItem key={role} value={role}>{role}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ACTION Section */}
                    <div className="space-y-2">
                        <Label htmlFor="action" className="text-base font-semibold">What ACTION?</Label>
                        {!principalReady && (
                            <p className="text-xs text-amber-700 dark:text-amber-400">Select a principal above to choose an action.</p>
                        )}
                        {principalReady && whoType === 'ROLE' && (who === 'public' || who === 'visitor') && (
                            <p className="text-xs text-muted-foreground">
                                Public and visitor roles only show read-oriented actions.
                            </p>
                        )}
                        <Select
                            value={action}
                            onValueChange={(value: any) => setAction(value)}
                            disabled={!principalReady}
                        >
                            <SelectTrigger
                                id="action"
                                className="h-auto min-h-12 py-2 [&>span]:block [&>span]:text-left [&>span]:truncate"
                            >
                                <SelectValue className="pl-1" />
                            </SelectTrigger>
                            <SelectContent>
                                {groupedAllowedActions.publicAudience.length > 0 && (
                                    <SelectGroup>
                                        <SelectLabel>Public audience (unauthenticated)</SelectLabel>
                                        {groupedAllowedActions.publicAudience.map((act) => (
                                            <SelectItem key={act} value={act}>{renderActionOption(act)}</SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}
                                {groupedAllowedActions.publicAudience.length > 0 && groupedAllowedActions.allUsersAudience.length > 0 && (
                                    <SelectSeparator />
                                )}
                                {groupedAllowedActions.allUsersAudience.length > 0 && (
                                    <SelectGroup>
                                        <SelectLabel>All users audience (authenticated)</SelectLabel>
                                        {groupedAllowedActions.allUsersAudience.map((act) => (
                                            <SelectItem key={act} value={act}>{renderActionOption(act)}</SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}
                                {(groupedAllowedActions.publicAudience.length > 0 || groupedAllowedActions.allUsersAudience.length > 0) && groupedAllowedActions.formsConfig.length > 0 && (
                                    <SelectSeparator />
                                )}
                                {groupedAllowedActions.formsConfig.length > 0 && (
                                    <SelectGroup>
                                        <SelectLabel>Form configuration (public_forms)</SelectLabel>
                                        {groupedAllowedActions.formsConfig.map((act) => (
                                            <SelectItem key={act} value={act}>{renderActionOption(act)}</SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}
                                {(groupedAllowedActions.publicAudience.length > 0 || groupedAllowedActions.allUsersAudience.length > 0 || groupedAllowedActions.formsConfig.length > 0) && groupedAllowedActions.general.length > 0 && (
                                    <SelectSeparator />
                                )}
                                {groupedAllowedActions.general.length > 0 && (
                                    <SelectGroup>
                                        <SelectLabel>General actions</SelectLabel>
                                        {groupedAllowedActions.general.map((act) => (
                                            <SelectItem key={act} value={act}>{renderActionOption(act)}</SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}
                                {groupedAllowedActions.legacy.length > 0 && (
                                    <>
                                        <SelectSeparator />
                                        <SelectGroup>
                                            <SelectLabel>Legacy actions (existing rows only)</SelectLabel>
                                            {groupedAllowedActions.legacy.map((act) => (
                                                <SelectItem key={act} value={act}>{renderActionOption(act)}</SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </>
                                )}
                            </SelectContent>
                        </Select>
                        {actionHelp && (
                            <p className="text-xs text-muted-foreground">{actionHelp}</p>
                        )}
                    </div>

                    {/* WHAT Section */}
                    <div className={`space-y-2 ${!principalReady ? 'opacity-60' : ''}`}>
                        <Label className="text-base font-semibold">On WHAT resource?</Label>
                        {!principalReady && (
                            <p className="text-xs text-muted-foreground">Complete the principal to edit resource scope.</p>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="whatType">Type</Label>
                                <Select
                                    value={whatType}
                                    onValueChange={(value: any) => {
                                        setWhatType(value);
                                        if (value === 'NewCollection') setWhat('*');
                                    }}
                                    disabled={!principalReady}
                                >
                                    <SelectTrigger id="whatType" className="[&>span]:block [&>span]:text-left [&>span]:truncate">
                                        <SelectValue className="text-left" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {allowedWhatTypes.map(type => (
                                            <SelectItem key={type} value={type}>
                                                {WHAT_TYPE_LABELS[type] || type}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="what">Resource ID(s)</Label>
                                {isCreateCollectionAction ? (
                                    <p className="text-sm text-muted-foreground py-2">
                                        Collection ID not applicable (applies to any new collection)
                                    </p>
                                ) : isConnectorSelectorType ? (
                                    <>
                                        <SearchableSelect
                                            value={what}
                                            onSelect={setWhat}
                                            options={connectorOptions}
                                            placeholder="Select a connector"
                                            emptyText="No connectors found."
                                            disabled={!principalReady}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Grants access to all remote collections on this connector. Use * for all connectors.
                                        </p>
                                    </>
                                ) : isFormSelectorType ? (
                                    <>
                                        <ItemSelectorField
                                            multiple={isWhatSetType}
                                            items={formSelectorItems}
                                            value={formSelectorValue}
                                            disabled={!principalReady}
                                            title={isWhatSetType ? 'Select form configurations' : 'Select a form configuration'}
                                            placeholder={
                                                isWhatSetType
                                                    ? 'Select form configurations…'
                                                    : 'Select a form configuration'
                                            }
                                            tableColumns={FORM_SELECTOR_TABLE_COLUMNS}
                                            defaultViewMode="table"
                                            viewModeStorageKey="acl.permission.form"
                                            searchPlaceholder="Search forms…"
                                            emptyMessage="No form configs found."
                                            onChange={(next) => {
                                                if (Array.isArray(next)) {
                                                    setWhat(formatWhatSetValue(next));
                                                    return;
                                                }
                                                setWhat(typeof next === 'string' ? next : '');
                                            }}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Form configuration IDs from public_forms (not collection item IDs)
                                        </p>
                                    </>
                                ) : isCollectionSelectorType ? (
                                    <>
                                        <ItemSelectorField
                                            multiple={isWhatSetType}
                                            items={componentSelectorItems}
                                            value={collectionSelectorValue}
                                            disabled={!principalReady}
                                            title={
                                                isWhatSetType
                                                    ? 'Select collections'
                                                    : 'Select a collection'
                                            }
                                            placeholder={
                                                isWhatSetType
                                                    ? 'Select collections…'
                                                    : 'Select a component'
                                            }
                                            tableColumns={COLLECTION_SELECTOR_TABLE_COLUMNS}
                                            defaultViewMode="table"
                                            viewModeStorageKey="acl.permission.collection"
                                            searchPlaceholder="Search by name, slug, or UID…"
                                            emptyMessage="No components found."
                                            onChange={(next) => {
                                                if (Array.isArray(next)) {
                                                    setWhat(formatWhatSetValue(next));
                                                    return;
                                                }
                                                setWhat(typeof next === 'string' ? next : '');
                                            }}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            {isWhatSetType
                                                ? 'Search and check collections to include in this permission set.'
                                                : 'Use * for wildcard (all resources)'}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <Input
                                            id="what"
                                            value={what}
                                            onChange={(e) => setWhat(e.target.value)}
                                            placeholder={
                                                whatType.includes('Set')
                                                    ? 'id1, id2, id3 (comma separated)'
                                                    : '* or specific_id'
                                            }
                                            required
                                            disabled={!principalReady}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Use * for wildcard (all resources)
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {(action === 'field_read' || action === 'field_update' || action === 'public_expose_fields' || action === 'all_users_expose_fields') && (
                        <div className="space-y-4 p-4 bg-gray-50 rounded">
                            <Label className="text-base font-semibold">Include fields (allowlist)</Label>
                            <p className="text-sm text-muted-foreground">
                                Leave none selected to allow all fields for this action. Choose a collection target above to load field names.
                            </p>

                            <div className="flex flex-wrap items-center gap-2">
                                <Popover open={fieldPickerOpen} onOpenChange={setFieldPickerOpen} modal={false}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            role="combobox"
                                            disabled={!principalReady || collectionFieldPickOptions.length === 0}
                                            className="min-w-[220px] justify-between"
                                        >
                                            {collectionFieldPickOptions.length === 0
                                                ? 'No fields (set collection target)'
                                                : 'Select fields'}
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="z-[100002] flex max-h-[min(340px,70vh)] w-[320px] flex-col overflow-hidden p-0"
                                        align="start"
                                        onWheel={(e) => e.stopPropagation()}
                                    >
                                        <Command className="flex max-h-[min(340px,70vh)] min-h-0 flex-col overflow-hidden rounded-md bg-popover text-popover-foreground">
                                            <CommandInput placeholder="Search fields…" />
                                            <CommandList className="min-h-0 flex-1 overflow-y-auto overscroll-contain scroll-smooth">
                                                <CommandEmpty>No matching fields.</CommandEmpty>
                                                {collectionFieldPickOptions.map((opt) => (
                                                    <CommandItem
                                                        key={opt.value}
                                                        value={`${opt.value} ${opt.label}`}
                                                        onSelect={() => toggleIncludeField(opt.value)}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                'mr-2 h-4 w-4',
                                                                includeFields.includes(opt.value) ? 'opacity-100' : 'opacity-0'
                                                            )}
                                                        />
                                                        <span className="truncate">{opt.label}</span>
                                                        <span className="ml-1 text-xs text-muted-foreground font-mono">{opt.value}</span>
                                                    </CommandItem>
                                                ))}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                {includeFields.length > 0 && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setIncludeFields([])}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </div>

                            {includeFields.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {includeFields.map((field) => (
                                        <Badge key={field} variant="secondary" className="flex items-center gap-1">
                                            {field}
                                            <X
                                                className="w-3 h-3 cursor-pointer"
                                                onClick={() => removeIncludeField(field)}
                                            />
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-4">
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Saving...' : (permission ? 'Update' : 'Create')}
                        </Button>
                        <Button type="button" variant="outline" onClick={onCancel}>
                            Cancel
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
};
